import CSVFileValidator from 'csv-file-validator';
import Papa from 'papaparse';
import axios from 'axios';
import { getExpressAPIHost } from '../../../utils';
import moment from 'moment-timezone';

const formatErrorMessages = (errors) => {
    const formattedErrors = [];
    const requiredFieldMessages = [];
    const missingFieldRows = new Set();

    errors.forEach(({ message, rowIndex }) => {
        if (message.startsWith('Number of fields mismatch') && typeof rowIndex === 'number') {
            missingFieldRows.add(rowIndex + 1);
            return;
        }

        const headerSuggestionMatch = message.match(/Header name\s+is not correct or missing in the \d+ row \/ (\d+) column\. The Header name should be (.+)/);
        if (headerSuggestionMatch) {
            const [, columnIndex, expectedName] = headerSuggestionMatch;
            formattedErrors.push(`Column ${columnIndex} in the header row is missing or incorrect. It should be named "${expectedName}".`);
            return;
        }

        const headerMatch = message.match(/Header name (.+?) is not correct or missing/);
        if (headerMatch) {
            const colName = headerMatch[1];
            formattedErrors.push(`The file must include a column named "${colName}". Please use the "Download Schedule Template" button to ensure the correct format.`);
            return;
        }

        const requiredMatch = message.match(/(.+?) is required in the (\d+) row \/ (\d+) column/);
        if (requiredMatch) {
            const [, columnName, row, column] = requiredMatch;
            requiredFieldMessages.push(`Missing value for "${columnName}" in row ${row}, column ${column}.`);
            return;
        }

        const invalidValueMatch = message.match(/(.+?) in row (\d+) is not valid/);
        if (invalidValueMatch) {
            const [, columnName, row] = invalidValueMatch;
            formattedErrors.push(`"${columnName}" in row ${row} contains an invalid value.`);
            return;
        }

        // fallback to original error message
        formattedErrors.push(message);
    });

    if (missingFieldRows.size) {
        formattedErrors.push(`One or more rows have the wrong number of columns (rows: ${[...missingFieldRows].join(', ')}).`);
    }

    return [...formattedErrors, ...requiredFieldMessages];
};

const readCSVHeaderRow = (file) =>
    new Promise((resolve) => {
        Papa.parse(file, {
            preview: 1,
            skipEmptyLines: true,
            complete: (results) => resolve(results.data?.[0] ?? []),
            error: () => resolve([]),
        });
    });

// csv-file-validator matches headers to the config strictly by position, so a file with
// reordered or extra columns (e.g. the schedule exports' conditional "Delivery Hauler" /
// "Updated At" columns) fails even though every expected column is present. The backends
// parse uploads by header name, so reorder the config to match the file's actual columns
// and only report columns that are genuinely absent.
export const alignConfigWithFileHeaders = (csvConfig, fileHeaders) => {
    const configHeadersByName = new Map(csvConfig.headers.map((header) => [header.name.toLowerCase(), header]));
    const matchedNames = new Set();

    const headers = fileHeaders.map((rawName) => {
        const name = String(rawName ?? '')
            .replace(/^\uFEFF/, '')
            .trim();
        const configHeader = configHeadersByName.get(name.toLowerCase());
        if (configHeader) {
            matchedNames.add(configHeader.name);
            return { ...configHeader, name };
        }
        // Column the config doesn't know about: accept it without validation
        return { name, inputName: name };
    });

    const missingColumnErrors = csvConfig.headers.filter((header) => !matchedNames.has(header.name)).map((header) => `The file must include a column named "${header.name}". Please use the "Download Schedule Template" button to ensure the correct format.`);

    return { alignedConfig: { ...csvConfig, headers }, missingColumnErrors };
};

export const submitCSV =
    (query, csvConfig, maxLength) =>
    async ({ file }) => {
        const fileHeaders = await readCSVHeaderRow(file[0]);
        const { alignedConfig, missingColumnErrors } = alignConfigWithFileHeaders(csvConfig, fileHeaders);

        if (missingColumnErrors.length) {
            return { error: missingColumnErrors };
        }

        const { data, inValidData } = await CSVFileValidator(file[0], alignedConfig);

        if (inValidData.length) {
            return {
                error: formatErrorMessages(inValidData),
            };
        }
        if (maxLength && data.length > maxLength) {
            return { error: `CSV file can have a maximum of ${maxLength} lines` };
        }

        try {
            const formData = new FormData();

            formData.append('file', file[0]);
            const returnData = await query(formData);

            return returnData;
        } catch (error) {
            return { error };
        }
    };

export const uniqueError = (header, row) => `${header} in row ${row} is not unique`;
export const validateError = (header, row) => `${header} in row ${row} is not valid`;
export const isStringInList = (value, list, nameProperty = 'name', allowEmpty = false) => {
    if ((allowEmpty && !value) || (allowEmpty && value === '')) {
        return true;
    }
    if (!list) {
        return false;
    }
    if (value.includes(' - ')) {
        // if value includes ' - ', check if the name before the dash is in the list
        const lastDashIndex = value.lastIndexOf(' - ');
        const name = value.substring(0, lastDashIndex);
        // In the case value = 'PF - Woodbury', there is no license number, so we need to check if the full name string is in the list
        if (list.some((item) => item[nameProperty]?.toLowerCase() === name?.toLowerCase())) return true;
    }
    return list.some((item) => item[nameProperty]?.toLowerCase() === value?.toLowerCase());
};
export const isNumber = (value) => !Number.isNaN(value);
export const isTime = (value) => moment(value, 'HH:mm', true).isValid();
export const isDateTime = (value, format = 'YYYY-MM-DD HH:mm:ss') => moment(value, format, true).isValid();
export const isRangeFormat = (value) => {
    const [start, end] = value.split('-');
    return isTime(start) && isTime(end) && start < end;
};
export const arePropertiesInList = (value, list, properties) => list.some((item) => properties.some((property) => item[property]?.toLowerCase() === value.toLowerCase()));
// TODO: Update regex
export const isLTValid = (lt_number) => true;
export const isDayOfWeek = (value) => ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].includes(value);

export const downloadCSVTemplate = (csvConfig, title) => {
    const csvHeaders = csvConfig.headers.map((header) => header.name).join(',');

    const blob = new Blob([`${csvHeaders}\n`], { type: 'text/csv' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${title}_Template.csv`;

    link.click();
    URL.revokeObjectURL(link.href);
};

export const postCSV = async (endpoint, formData) => {
    try {
        const response = await axios({
            method: 'post',
            url: `${getExpressAPIHost()}${endpoint}`,
            data: formData,
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    } catch (error) {
        return { error: error?.response?.data?.message || 'Unable to upload CSV.' };
    }
};

export default submitCSV;
